import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  DatePicker,
  Select,
  Button,
  Space,
  Tabs,
  Table,
  Statistic,
  Spin,
  message,
  Empty
} from 'antd';
import {
  BarChartOutlined,
  DollarOutlined,
  UserOutlined,
  ShopOutlined,
  TrophyOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { analyticsAPI, stationOwnerAPI } from '../services/api';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [groupBy, setGroupBy] = useState<'day' | 'month' | 'year'>('day');
  const [revenueData, setRevenueData] = useState<any>(null);
  const [customerData, setCustomerData] = useState<any>(null);
  const [stations, setStations] = useState<any[]>([]);
  const [selectedStation, setSelectedStation] = useState<string>('all');
  const [stationPerformance, setStationPerformance] = useState<any>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, groupBy]);

  useEffect(() => {
    if (selectedStation !== 'all') {
      loadStationPerformance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStation, dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [revenue, customer, stationsList] = await Promise.all([
        analyticsAPI.getRevenueReport(
          dateRange[0].format('YYYY-MM-DD'),
          dateRange[1].format('YYYY-MM-DD'),
          groupBy
        ),
        analyticsAPI.getCustomerAnalytics(),
        stationOwnerAPI.getStations()
      ]);
      setRevenueData(revenue);
      setCustomerData(customer);
      setStations(stationsList);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const loadStationPerformance = async () => {
    if (selectedStation === 'all') return;
    setLoading(true);
    try {
      const data = await analyticsAPI.getStationPerformance(
        selectedStation,
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );
      setStationPerformance(data);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İstasyon performans verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (type: 'pdf' | 'excel') => {
    message.info(`${type.toUpperCase()} export özelliği yakında eklenecek`);
  };

  const revenueColumns = [
    {
      title: 'Tarih',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: 'Gelir',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (value: number) => `${value.toLocaleString('tr-TR')} ₺`,
    },
    {
      title: 'İşlem Sayısı',
      dataIndex: 'transactions',
      key: 'transactions',
    },
    {
      title: 'Ortalama İşlem',
      dataIndex: 'averageTransaction',
      key: 'averageTransaction',
      render: (value: number) => `${value.toLocaleString('tr-TR')} ₺`,
    },
  ];

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: '24px' }}>
            <BarChartOutlined /> Analitik ve Raporlar
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
            Detaylı performans analizi ve raporlar
          </Text>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Space wrap style={{ width: '100%' }}>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                style={{ width: '100%', maxWidth: 250 }}
              />
              <Select
                value={groupBy}
                onChange={setGroupBy}
                style={{ width: '100%', maxWidth: 120 }}
              >
                <Option value="day">Günlük</Option>
                <Option value="month">Aylık</Option>
                <Option value="year">Yıllık</Option>
              </Select>
            </Space>
            <Space wrap style={{ width: '100%' }}>
              <Button icon={<ReloadOutlined />} onClick={loadData}>
                Yenile
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => handleExport('pdf')}>
                PDF İndir
              </Button>
            </Space>
          </Space>
        </Col>
      </Row>

      {/* Özet İstatistikler */}
      {revenueData && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam Gelir"
                value={revenueData.summary?.totalRevenue || 0}
                prefix={<DollarOutlined />}
                suffix="₺"
                precision={2}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam İşlem"
                value={revenueData.summary?.totalTransactions || 0}
                prefix={<ShopOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Ortalama İşlem"
                value={revenueData.summary?.averageTransaction || 0}
                prefix={<DollarOutlined />}
                suffix="₺"
                precision={2}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Ortalama Değerlendirme"
                value={customerData?.ratings?.[0]?.rating || 0}
                prefix={<TrophyOutlined />}
                suffix="/ 5.0"
                precision={1}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Tabs defaultActiveKey="revenue">
        <TabPane
          tab={
            <span>
              <DollarOutlined />
              Gelir Analizi
            </span>
          }
          key="revenue"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Card title="Gelir Trendi">
                {revenueData?.data && revenueData.data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={revenueData.data}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#1890ff" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(value: any) => [`${value.toLocaleString('tr-TR')} ₺`, 'Gelir']} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#1890ff"
                        fillOpacity={1}
                        fill="url(#colorRevenue)"
                        name="Gelir (₺)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="Veri bulunamadı" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="İşlem Dağılımı">
                {revenueData?.data && revenueData.data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={revenueData.data.slice(-7)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="transactions" fill="#52c41a" name="İşlem Sayısı" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="Veri bulunamadı" />
                )}
              </Card>
            </Col>
          </Row>

          <Card title="Detaylı Gelir Raporu" style={{ marginTop: 16 }} styles={{ body: { padding: '16px' } }}>
            <Table
              columns={revenueColumns}
              dataSource={revenueData?.data || []}
              rowKey="period"
              pagination={{ pageSize: 10 }}
              loading={loading}
              scroll={{ x: 'max-content' }}
              size="small"
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <UserOutlined />
              Müşteri Analizi
            </span>
          }
          key="customers"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Değerlendirme Dağılımı">
                {customerData?.ratings && customerData.ratings.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={customerData.ratings}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry: any) => `${entry.rating} Yıldız: ${entry.count}`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {customerData.ratings.map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="Değerlendirme verisi yok" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="En Çok Ziyaret Edilen İstasyonlar">
                {customerData?.topStations && customerData.topStations.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={customerData.topStations} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={150} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="visits" fill="#1890ff" name="Ziyaret Sayısı" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="Veri bulunamadı" />
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane
          tab={
            <span>
              <ShopOutlined />
              İstasyon Performansı
            </span>
          }
          key="stations"
        >
          <Card
            title="İstasyon Performans Analizi"
            extra={
              <Select
                value={selectedStation}
                onChange={setSelectedStation}
                style={{ width: 200 }}
                placeholder="İstasyon seçin"
              >
                <Option value="all">Tüm İstasyonlar</Option>
                {stations.map((station: any) => (
                  <Option key={station._id} value={station._id}>
                    {station.name}
                  </Option>
                ))}
              </Select>
            }
          >
            {selectedStation === 'all' ? (
              <Empty description="Lütfen bir istasyon seçin" />
            ) : stationPerformance ? (
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <Card title="Gelir Trendi">
                    {stationPerformance.revenueData && stationPerformance.revenueData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={stationPerformance.revenueData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="revenue" stroke="#1890ff" name="Gelir (₺)" />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <Empty description="Gelir verisi yok" />
                    )}
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="Değerlendirme Trendi">
                    {stationPerformance.ratingData && stationPerformance.ratingData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={stationPerformance.ratingData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis domain={[0, 5]} />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="averageRating" stroke="#52c41a" name="Ortalama Değerlendirme" />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <Empty description="Değerlendirme verisi yok" />
                    )}
                  </Card>
                </Col>
              </Row>
            ) : (
              <Spin />
            )}
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Analytics;

